import { cfetch } from '@confluence/network';

import type {
	AudioRequest,
	AudioBriefingRequest,
	AudioNarrationRequest,
} from '../types/audioRequestType';
import type { CustomizationType } from '../types/customizationTypes';

export const AUDIO_SUMMARY_API_ROOT = '/gateway/api/v1/audio/summary';
export const AUDIO_NARRATION_API_ROOT = '/gateway/api/v1/audio/narration';
export const AUDIO_BRIEFING_API_ROOT = '/gateway/api/v1/audio/briefing';
export const POPULAR_BRIEFING_API_ROOT = '/gateway/api/v1/audio/briefing/popular';

export const getSpeech = async ({
	pageAri,
	locale,
	activationId,
}: AudioRequest): Promise<ReadableStream<Uint8Array>> => {
	const response = await cfetch(AUDIO_SUMMARY_API_ROOT, {
		method: 'POST',
		body: JSON.stringify({
			resource_ari: pageAri,
			locale,
		}),
		headers: {
			'Content-Type': 'application/json',
			'ATL-ActivationId': activationId,
		},
	});

	if (!response.body) {
		throw new Error('Response body is null');
	}

	return response.body;
};

export const getNarration = async ({
	activeContentAri,
	locale,
	activationId,
}: AudioNarrationRequest): Promise<ReadableStream<Uint8Array>> => {
	const response = await cfetch(AUDIO_NARRATION_API_ROOT, {
		method: 'POST',
		body: JSON.stringify({
			resource_ari: activeContentAri,
			locale,
			transcriber_model: 'TEXT_ONLY',
		}),
		headers: {
			'Content-Type': 'application/json',
			'ATL-ActivationId': activationId,
		},
	});

	if (!response.body) {
		throw new Error('Response body is null');
	}

	return response.body;
};

export const getBriefing = async ({
	activeContentAri,
	additionalResources,
	locale,
	activationId,
	customization,
}: AudioBriefingRequest): Promise<ReadableStream<Uint8Array>> => {
	const additionalResourcesBody = additionalResources.map((resource) => ({
		resource_ari: resource.ari,
		activation_id: activationId,
	}));

	const response = await cfetch(AUDIO_BRIEFING_API_ROOT, {
		method: 'POST',
		body: JSON.stringify({
			resources: [
				{
					resource_ari: activeContentAri,
					activation_id: activationId,
				},
				...additionalResourcesBody,
			],
			locale,
			user_instruction: customization,
		}),
		headers: {
			'Content-Type': 'application/json',
			'ATL-ActivationId': activationId,
		},
	});

	if (!response.body) {
		throw new Error('Response body is null');
	}

	return response.body;
};

export const getPopularBriefing = async (
	activationId: string,
	locale: string,
	customization: CustomizationType,
) => {
	const response = await cfetch(POPULAR_BRIEFING_API_ROOT, {
		method: 'POST',
		body: JSON.stringify({
			locale,
			user_instruction: customization,
		}),
		headers: {
			'Content-Type': 'application/json',
			'ATL-ActivationId': activationId,
		},
	});

	if (!response.body) {
		throw new Error('Response body is null');
	}

	return response.body;
};
